import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Box,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  Slider,
  CircularProgress,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { InfoIcon } from "../../Icons/InfoIcon";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as _ser from "../../_redux/_services/ApplicantsSlice";
import CloseIcon from "@mui/icons-material/Close";
import { updateSnackbar } from "../../_redux/SnackbarSlice";
import { useLocation, useNavigate } from "react-router-dom";

const initalState = { data: null, message: "", isloader: true };

/* ---------------- ADVANCED SEARCH MODEL ---------------- */
export const advanceSearchModel = [
  {
    id: 1,
    displayName: "Skills",
    name: "skills",
    isActive: false,
    type: "multiselect",
    helperText:
      "Add one or more skills. Multiple entries are combined with AND (candidates must have all skills)",
  },
  {
    id: 2,
    displayName: "Job Title",
    name: "jobtitle",
    isActive: false,
    type: "multiselect",
    helperText:
      "Enter one or more job titles. Multiple entries are combined with OR (candidates can match any title)",
  },
  {
    id: 3,
    displayName: "Experience",
    name: "experience",
    isActive: false,
    type: "rangeText",
    helperText: "Filter candidates by years of experience. Example: 3–5 years",
  },
  {
    id: 4,
    displayName: "Location",
    name: "location",
    isActive: false,
    type: "locationRange",
    helperText:
      "Search by city, state, or country. Multiple locations can be entered, separated by commas",
  },
  {
    id: 5,
    displayName: "Keyword",
    name: "keyword",
    isActive: false,
    type: "text",
    helperText:
      "Enter keywords to search across resumes and profiles. Use quotes for exact phrases",
  },
  {
    id: 6,
    displayName: "Boolean Search",
    name: "boolean",
    isActive: false,
    type: "text",
    helperText:
      "Use Boolean operators (AND, OR, NOT) for complex searches. Example: Java AND Spring NOT Python",
  },
  {
    id: 7,
    displayName: "Recruiter",
    name: "recruiter",
    isActive: false,
    type: "autoCompleteSingle",
    helperText: "Shows applicants uploaded by the selected recruiter",
  },
];

const initalFilter = {
  yearsExperienceMin: "",
  yearsExperienceMax: "",
  boolean: "",
  keyword: "",
  recruiterId: "",
};

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 200,
    maxHeight: 200,
    fontSize: 13,
    backgroundColor: "#2B2B2B",
    fontWeight: 400,
  },
});

const AdvancedSearch = ({
  onApplyFilter,
  onClose,
  isOpen,
  customFieldsData,
  recruiterList,
  storedTheme,
  onClearFilters,
}) => {
  const theme = useTheme();

  const [advanceSearchlist, setAdvanceSearchlist] =
    useState(advanceSearchModel);

  const [skillsValue, setSkillsValue] = useState("");
  const [skillsObj, setSkillsObj] = useState([]);
  const [titleValue, setTitleValue] = useState("");
  const [titleObj, setTitleObj] = useState([]);

  const [filters, setFilters] = useState(initalFilter);
  const [statusObj, setStatusObj] = useState({});
  const [savedAdvancedSearch, setSavedAdvancedSearch] = useState({});

  const [locationValue, setLocationValue] = useState({
    id: 0,
    location: "",
    type: "",
  });
  const [locationRadius, setLocationRadius] = useState(50);
  const [loading, setLoading] = useState(false);
  const [locationsOptions, setLocationsOptions] = useState([]);
  const [recruiterObj, setRecruiterObj] = useState([]);
  const [fieldOptions, setFieldOptions] = useState({});
  const [searchablefields, setSearchablefields] = useState([]);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const filterObj = filters;

  const workModelModel = [
    { id: 1, name: "All", isActive: false },
    { id: 2, name: "On-site", isActive: "false" },
    { id: 3, name: "Contract", isActive: "false" },
    { id: 4, name: "Internship", isActive: "false" },
    { id: 5, name: "Fulltime", isActive: "false" },
  ];

  const jobStatusModel = [
    { id: 1, name: "All", isActive: false },
    { id: 2, name: "PartTime", isActive: "false" },
    { id: 3, name: "Contract", isActive: "false" },
    { id: 4, name: "Internship", isActive: "false" },
    { id: 5, name: "Fulltime", isActive: "false" },
  ];

  const clientsModel = [
    { id: 1, name: "ABC", isActive: false },
    { id: 2, name: "BCD", isActive: false },
  ];

  const advanceSearchParams = useSelector(
    (state) => state.Highlight.initialParams,
  );

  useEffect(() => {
    if (!advanceSearchParams || Object.keys(advanceSearchParams).length === 0) {
      // Filters were cleared elsewhere (e.g. after saving an edit) — mirror
      // that reset here so the panel doesn't keep showing stale selections.
      setAdvanceSearchlist((prev) =>
        prev.some((f) => f.isActive)
          ? prev.map((f) => ({ ...f, isActive: false }))
          : prev,
      );
      setSkillsObj([]);
      setTitleObj([]);
      setStatusObj({});
      setFilters(JSON.parse(JSON.stringify(initalFilter)));
      setLocationValue({ id: 0, location: "", type: "" });
      setLocationRadius(50);
      setSavedAdvancedSearch({});
      return;
    }
    const {
      jobTitle,
      skills,
      custom,
      Candidatestatus,
      Source,
      yearsExperienceMin,
      yearsExperienceMax,
      boolean,
      keyword,
      recruiterId,
      locationId,
      type,
      location,
      radius,
    } = advanceSearchParams;
    // Copy advanceSearchModel to avoid mutating original
    const updatedFields = advanceSearchlist.map((f) => {
      const fieldName = f.name.toLowerCase();
      // Check if corresponding data is available
      if (
        (fieldName === "jobtitle" && jobTitle) ||
        (fieldName === "skills" && skills) ||
        (fieldName === "candidate status" && Candidatestatus) ||
        (fieldName === "source" && Source) ||
        (fieldName === "boolean" && boolean) ||
        (fieldName === "keyword" && keyword) ||
        (fieldName === "recruiter" && recruiterId) ||
        (fieldName === "location" && locationId)
      ) {
        return { ...f, isActive: true };
      }
      return f; // keep original
    });
    setAdvanceSearchlist(updatedFields);

    if (jobTitle) {
      setTitleObj(Array.isArray(jobTitle) ? jobTitle : jobTitle.split(","));
    }
    if (skills) {
      setSkillsObj(Array.isArray(skills) ? skills : skills.split(","));
    }
    if (custom || Candidatestatus || Source) {
      const restoredStatus = {};
      if (Array.isArray(custom)) {
        custom.forEach(({ key, value }) => {
          if (key !== "documentType") {
            const field = updatedFields.find(
              (f) => f.name === key || f.displayName === key,
            );
            if (field && (field.type === "singleSelect" || field.type === "text1")) {
              restoredStatus[key] = value;
            } else {
              restoredStatus[key] =
                typeof value === "string" ? value.split(",") : value;
            }
          } else {
            restoredStatus["Document type"] = value;
          }
        });
      }
      if (Candidatestatus) {
        let defaultOptions;
        let finalOptions = [];
        let index = searchablefields.findIndex(
          (field) => field.fieldName === "Candidate status",
        );
        if (index !== -1) {
          defaultOptions = searchablefields[index].customValues;
          Candidatestatus.map((s) => {
            defaultOptions.map((o) => {
              if (s === o.id) {
                finalOptions.push(o);
              }
            });
          });
        }
        restoredStatus["Candidate status"] = finalOptions;
      }
      if (Source) {
        restoredStatus["Source"] = Array.isArray(Source) ? Source : [Source];
      }
      setStatusObj(restoredStatus);
      // console.log(restoredStatus);
    }
    setFilters((prev) => ({
      ...prev,
      yearsExperienceMin: yearsExperienceMin ?? "",
      yearsExperienceMax: yearsExperienceMax ?? "",
      boolean: boolean ?? "",
      keyword: keyword ?? "",
      recruiterId: recruiterId ? String(recruiterId) : "",
    }));

    if (locationId) {
      setLocationValue({ id: locationId, type, location });
      setLocationRadius(radius ?? 50);
    }
  }, [advanceSearchParams]);

  // Initialize custom fields
  useEffect(() => {
    if (customFieldsData && customFieldsData.length > 0) {
      const data = JSON.parse(JSON.stringify(customFieldsData));
      let customSearchableFields = data.filter(
        (x) => x.moduleId.toString() === "2" && x.searchableField === 1,
      );
      if (customSearchableFields.length > 0) {
        let array = [...advanceSearchModel];
        customSearchableFields.forEach((list) => {
          let obj = {};
          list.customValues = JSON.parse(list.customValues);
          if (list) {
            obj["id"] = array.length + 1;
            obj["displayName"] = list.fieldName;
            obj["name"] = list.fieldName;
            obj["isActive"] = false;
            obj["helperText"] = list.helperText
              ? list.helperText
              : "Custom field";
            obj["type"] =
              list.dataType === "Text box"
                ? "text1"
                : list.dataType === "Multi select"
                  ? "options"
                  : list.fieldName === "Candidate status"
                    ? "autoCompleteMulti"
                    : list.fieldName === "Source"
                      ? "autoCompleteSingle"
                      : list.dataType === "Single select"
                        ? "singleSelect"
                        : "";
          }
          array.push(obj);
        });
        // Preserve isActive for fields with data in advanceSearchParams
        setAdvanceSearchlist((prev) => {
          if (!prev || prev.length === 0) return array;
          return array.map((f) => {
            const prevField = prev.find((p) => p.name === f.name);
            return prevField && prevField.isActive
              ? { ...f, isActive: true }
              : f;
          });
        });
      } else {
        setAdvanceSearchlist((prev) => {
          if (!prev || prev.length === 0) return advanceSearchModel;
          return advanceSearchModel.map((f) => {
            const prevField = prev.find((p) => p.name === f.name);
            return prevField && prevField.isActive
              ? { ...f, isActive: true }
              : f;
          });
        });
      }
      setSearchablefields(customSearchableFields);
    } else {
      setAdvanceSearchlist((prev) => {
        if (!prev || prev.length === 0) return advanceSearchModel;
        return advanceSearchModel.map((f) => {
          const prevField = prev.find((p) => p.name === f.name);
          return prevField && prevField.isActive ? { ...f, isActive: true } : f;
        });
      });
    }
  }, [customFieldsData]);

  // Initialize locations
  const countriesData = useSelector((state) => state.CountriesListByValue.data);
  useEffect(() => {
    if (countriesData && countriesData.length > 0) {
      setLoading(false);
      setLocationsOptions(countriesData);
      dispatch(_ser.getCountriesListByValueStartReducer(initalState));
    } else if (countriesData && countriesData.length === 0) {
      setLocationsOptions([]);
      setLoading(false);
    }
  }, [countriesData]);

  // Initialize recruiters
  useEffect(() => {
    if (recruiterList?.length > 0) {
      const data = JSON.parse(JSON.stringify(recruiterList));
      let newArray = [];
      data.forEach((list) => {
        let obj = {
          id: list.id.toString(),
          name: `${list.firstName} ${list.lastName}`,
          emailId: list.emailId,
        };
        newArray.push(obj);
      });
      setRecruiterObj(newArray);
    }
  }, [recruiterList]);

  /* ---------- HANDLERS ---------- */
  const onActiveAccodianHandler = (item) => {
    setAdvanceSearchlist((prev) =>
      prev.map((x) => (x.id === item.id ? { ...x, isActive: !x.isActive } : x)),
    );
    if (
      item.type === "singleSelect" ||
      item.type === "options" ||
      item.type === "autoCompleteMulti" ||
      item.type === "autoCompleteSingle"
    ) {
      SelectOptionsHandler(item);
    }
  };

  const SelectOptionsHandler = (obj) => {
    let options = searchablefields.filter((x) => x.fieldName === obj.name);
    if (options.length > 0) {
      setFieldOptions((prev) => ({
        ...prev,
        [obj.name]: options[0]?.customValues,
      }));
    }
  };

  const onkeyhandlerhandler = (e, type) => {
    if (e.key === "Enter") {
      if (type === "skills" && skillsValue.trim()) {
        if (!skillsObj.includes(skillsValue.trim())) {
          setSkillsObj([...skillsObj, skillsValue.trim()]);
        }
        setSkillsValue("");
      }
      if (type === "jobtitle" && titleValue.trim()) {
        if (!titleObj.includes(titleValue.trim())) {
          setTitleObj([...titleObj, titleValue.trim()]);
        }
        setTitleValue("");
      }
    }
  };

  const onRemoveValueHandler = (e, name, index) => {
    e && e.stopPropagation && e.stopPropagation();
    if (name === "skills") {
      setSkillsObj((prev) => prev.filter((_, i) => i !== index));
    }
    if (name === "jobtitle") {
      setTitleObj((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const onChangeHandler = (value, name, type) => {
    if (type === "options" || type === "singleSelect" || type === "text1") {
      setStatusObj((prev) => ({ ...prev, [name]: value }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  function getStyles(name, personName, theme) {
    return {
      fontWeight:
        personName?.indexOf(name) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium,
    };
  }

  const handleClearFilters = () => {
    let data = JSON.parse(JSON.stringify(advanceSearchlist));
    data.forEach((list) => (list.isActive = false));
    setAdvanceSearchlist(data);
    setSavedAdvancedSearch({});
    setLocationsOptions([]);
    setSkillsObj([]);
    setStatusObj({});
    setTitleObj([]);
    setSkillsValue("");
    setTitleValue("");
    setFilters(JSON.parse(JSON.stringify(initalFilter)));
    setLocationValue({ id: 0, location: "", type: "" });
    setLocationRadius(50);
    navigate(location.pathname + location.search, {
      replace: true,
      state: null,
    });
    onClearFilters?.();
  };

  const handleApplyFilter = () => {
    let params = {},
      titleObject = titleObj,
      skillsObject = skillsObj;

    if (titleObject.length > 0) {
      params["jobTitle"] = titleObject.join(",");
    }
    if (skillsObject.length > 0) {
      params["skills"] = skillsObject.join(",");
    }
    if (statusObj) {
      let custom = [];
      let Keys = Object.keys(statusObj);
      Keys.forEach((list) => {
        if (
          statusObj[list]?.length > 0 &&
          list !== "Candidate status" &&
          list !== "Source"
        ) {
          let obj = {
            key:
              list === "Document type"
                ? "documentType"
                : `${list.replace(/ +/g, " ")}`,
            value: `${statusObj[list]}`,
          };
          custom.push(obj);
        } else if (list === "Candidate status" && statusObj[list].length > 0) {
          params[`${list.replace(/ +/g, "")}`] = statusObj[list].map(
            (x) => x.id,
          );
        } else if (list === "Source" && statusObj[list]) {
          params[`${list.replace(/ +/g, " ")}`] =
            typeof statusObj[list] === "string"
              ? statusObj[list]
              : statusObj[list][0];
        }
      });
      if (custom.length > 0) {
        params["custom"] = custom;
      }
    }
    if (filterObj.yearsExperienceMin !== "") {
      params["yearsExperienceMin"] = filterObj.yearsExperienceMin;
    }
    if (filterObj.yearsExperienceMax !== "") {
      params["yearsExperienceMax"] = filterObj.yearsExperienceMax;
    }
    if (filterObj?.boolean !== "") {
      params["boolean"] = filterObj.boolean;
    }
    if (filterObj?.keyword !== "") {
      params["keyword"] = filterObj.keyword;
    }
    if (locationValue.id !== 0) {
      params["locationId"] = locationValue.id;
      params["radius"] = locationRadius;
      params["type"] = locationValue?.type;
      params["location"] = locationValue?.location;
    }
    if (filterObj?.recruiterId && filterObj?.recruiterId !== "") {
      params["recruiterId"] = parseInt(filterObj.recruiterId);
    }
    if (Object.keys(params).length === 0) {
      dispatch(
        updateSnackbar({
          type: "error",
          message: "Select atleast one field to filter",
        }),
      );
    } else {
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
      onApplyFilter?.(params, savedAdvancedSearch);
    }
  };

  return (
    <>
      {isOpen && (
        <div style={{ width: "20%", paddingRight: "30px" }}>
          <div style={{ position: 'fixed', left: 0, width: 'inherit', zIndex: 1 }}>
            <CloseIcon
              onClick={() => onClose?.()}
              style={{
                position: "absolute",
                marginTop: "-60px",
                cursor: "pointer",
                zIndex: "9",
                color: "rgb(0, 0, 0)",
                width: "25%",
                right: "0",
              }}
            />
          </div>
          <div
            className="adSearchPannel"
            style={{ marginTop: "-100px", paddingTop: "105px" }}
          >
            <div className="adSearchList-container">
              <ul className="adSearchPannelList">
                {advanceSearchlist.map((item) => {
                  // item.isActive =
                  //   item.isActive === true
                  //     ? item.isActive
                  //     : jobTitleKeywords?.length > 0 &&
                  //         item.displayName === 'Job Title'
                  //       ? true
                  //       : skillsKeywords?.length > 0 &&
                  //           item.displayName === 'Skills'
                  //         ? true
                  //         : false;
                  return (
                    <li key={item.id}>
                      <div
                        className={item.isActive ? "active d-flex-space-between" : "d-flex-space-between"}
                        onClick={(e) => {
                          onActiveAccodianHandler(item);
                        }}
                      >
                        <div className="label-with-icon">
                          {item.displayName}
                          <CustomWidthTooltip
                            title={item.helperText}
                            placement="right-start"
                            arrow
                          >
                            <span style={{ height: "15px" }}>
                              <InfoIcon
                                width={20}
                                height={20}
                                primaryColor={storedTheme?.primaryColor}
                                primaryFontColor={storedTheme?.primaryFontColor}
                              />
                            </span>
                          </CustomWidthTooltip>
                        </div>
                        <span>{item.isActive ? "-" : "+"}</span>
                      </div>
                      {item.isActive ? (
                        <div style={{ marginTop: "10px" }}>
                          {item.type === "multiselect" ? (
                            <>
                              <input
                                placeholder={`Search for ${item.displayName}`}
                                className="adSearchBox"
                                value={
                                  item.name === "skills"
                                    ? skillsValue
                                    : titleValue
                                }
                                onKeyUp={(e) => {
                                  onkeyhandlerhandler(e, item.name);
                                }}
                                onChange={(e) => {
                                  item.name === "skills"
                                    ? setSkillsValue(e.target.value)
                                    : setTitleValue(e.target.value);
                                }}
                              />
                              <ul className="listbtns">
                                {item.name === "skills" &&
                                  skillsObj &&
                                  skillsObj.map((list, i) => (
                                    <li key={list}>
                                      {list}
                                      <span
                                        onClick={(e) =>
                                          onRemoveValueHandler(e, item.name, i)
                                        }
                                      >
                                        x
                                      </span>
                                    </li>
                                  ))}
                                {item.name === "jobtitle" &&
                                  titleObj &&
                                  titleObj.map((list, i) => (
                                    <li key={list}>
                                      {list}
                                      <span
                                        onClick={(e) =>
                                          onRemoveValueHandler(e, item.name, i)
                                        }
                                      >
                                        x
                                      </span>
                                    </li>
                                  ))}
                              </ul>
                            </>
                          ) : item.type === "text" || item.type === "text1" ? (
                            <input
                              placeholder={`Search for ${item.displayName}`}
                              className="adSearchBox"
                              value={
                                item.name === "boolean" ||
                                item.name === "keyword"
                                  ? filterObj[item.name]
                                  : statusObj[item.name]
                              }
                              onChange={(e) =>
                                onChangeHandler(
                                  e.target.value,
                                  item.name,
                                  item.type,
                                )
                              }
                            />
                          ) : item.type === "options" && item.isActive ? (
                            <div className="custom-multiSelect">
                              <FormControl sx={{ width: "100%" }}>
                                <Select
                                  labelId="demo-multiple-chip-label"
                                  id="demo-multiple-chip"
                                  multiple
                                  sx={{ height: "38px" }}
                                  name={item.name}
                                  value={statusObj?.[item.name] || []}
                                  onChange={(e) =>
                                    onChangeHandler(
                                      e.target.value,
                                      item.name,
                                      "options",
                                    )
                                  }
                                  displayEmpty
                                  renderValue={(selected) => {
                                    const arr = Array.isArray(selected)
                                      ? selected
                                      : selected
                                        ? String(selected).split(",")
                                        : [];

                                    if (arr.length === 0) {
                                      return (
                                        <span
                                          style={{
                                            color: "rgba(0,0,0,0.6)",
                                          }}
                                        >
                                          Select
                                        </span>
                                      );
                                    }
                                    return (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                        }}
                                      >
                                        {typeof selected !== "string" &&
                                          selected.map((value) => (
                                            <Chip
                                              key={value}
                                              label={
                                                value.includes("_")
                                                  ? value.replace("_", " ")
                                                  : value
                                              }
                                            />
                                          ))}
                                      </Box>
                                    );
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: { width: "inherit" },
                                    },
                                  }}
                                >
                                  {(fieldOptions[item.name] || []).map(
                                    (name) => (
                                      <MenuItem
                                        className="selectOptions"
                                        key={name.id ? name.id : name}
                                        style={getStyles(
                                          name,
                                          statusObj[item.name],
                                          theme,
                                        )}
                                        value={name.id ? name.id : name}
                                      >
                                        <span style={{ fontWeight: "400" }}>
                                          {" "}
                                          {name.id ? name.internal : name}{" "}
                                        </span>
                                      </MenuItem>
                                    ),
                                  )}
                                </Select>
                              </FormControl>
                            </div>
                          ) : item.type === "singleSelect" && item.isActive ? (
                            <div className="custom-select">
                              <FormControl sx={{ width: "100%" }}>
                                <Select
                                  id=""
                                  sx={{ height: "38px" }}
                                  name={item.name}
                                  value={statusObj?.[item.name] || ""}
                                  onChange={(e) =>
                                    onChangeHandler(
                                      e.target.value,
                                      item.name,
                                      "singleSelect",
                                    )
                                  }
                                  displayEmpty
                                  renderValue={(value) => {
                                    if (!value || (typeof value === "string" && value.length === 0) || (Array.isArray(value) && value.length === 0)) {
                                      return (
                                        <span
                                          style={{ color: "rgba(0,0,0,0.6)" }}
                                        >
                                          Select{" "}
                                        </span>
                                      );
                                    }
                                    return <span>{value}</span>;
                                  }}
                                >
                                  {(fieldOptions[item.name] || []).map(
                                    (name, idx) => (
                                      <MenuItem
                                        className="selectOptions"
                                        key={name.id || idx}
                                        value={name}
                                      >
                                        {name}
                                      </MenuItem>
                                    ),
                                  )}
                                </Select>
                              </FormControl>
                            </div>
                          ) : item.type === "options" &&
                            item.name === "Work Model" ? (
                            <ul className="listbtns">
                              {workModelModel?.map((list) => (
                                <li key={list.id} className="active">
                                  {list.name}
                                </li>
                              ))}
                            </ul>
                          ) : item.type === "options" &&
                            item.name === "Job Status" ? (
                            <ul className="listbtns">
                              {jobStatusModel.map((list) => (
                                <li key={list.id} className="active">
                                  {list.name}
                                </li>
                              ))}
                            </ul>
                          ) : item.type === "options" &&
                            item.name === "Client" ? (
                            <ul className="listbtns">
                              {clientsModel?.map((list) => (
                                <li key={list.id} className="active">
                                  {list.name}
                                </li>
                              ))}
                            </ul>
                          ) : item.type === "rangeText" ? (
                            <div className="rangeText">
                              <div className="suffixtext">
                                <input
                                  placeholder="Min"
                                  type="text"
                                  value={filterObj.yearsExperienceMin}
                                  onChange={(e) =>
                                    onChangeHandler(
                                      e.target.value,
                                      "yearsExperienceMin",
                                    )
                                  }
                                />
                                <span>Years</span>
                              </div>
                              <div className="suffixtext">
                                <input
                                  placeholder="Max"
                                  type="text"
                                  value={filterObj.yearsExperienceMax}
                                  onChange={(e) =>
                                    onChangeHandler(
                                      e.target.value,
                                      "yearsExperienceMax",
                                    )
                                  }
                                />
                                <span>Years</span>
                              </div>
                            </div>
                          ) : item.type === "locationRange" ? (
                            <div className="locationRange">
                              <div
                                className="boldlabel jobcode_search"
                                style={{ width: "100%" }}
                              >
                                <Autocomplete
                                  freeSolo
                                  loading={loading}
                                  options={locationsOptions || []}
                                  autoHighlight
                                  id="controlled-demo"
                                  autoComplete="off"
                                  clearOnEscape
                                  value={locationValue}
                                  getOptionLabel={(option) =>
                                    option.location || ""
                                  }
                                  blurOnSelect
                                  onInputChange={(event, value) => {
                                    if (event?._reactName === "onChange") {
                                      setLocationValue({
                                        id: 0,
                                        location: value
                                          ? value.trimStart()
                                          : "",
                                        type: "",
                                      });
                                      if (value.length > 2) {
                                        setLoading(true);
                                        const delayDebounceFn = setTimeout(
                                          () => {
                                            dispatch(
                                              _ser.getCountriesListByValueAsync(
                                                value.toLowerCase(),
                                              ),
                                            );
                                          },
                                          300,
                                        );
                                        return () =>
                                          clearTimeout(delayDebounceFn);
                                      }
                                    }
                                  }}
                                  onChange={(event, value) => {
                                    if (
                                      event.type === "click" ||
                                      event.type === "keydown" ||
                                      event._reactName === "onChange"
                                    ) {
                                      setLocationValue(
                                        value
                                          ? value
                                          : {
                                              id: 0,
                                              location: "",
                                              type: "",
                                            },
                                      );
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      error={
                                        locationValue.location === ""
                                          ? false
                                          : locationsOptions.length === 0 &&
                                              locationValue.location.length > 2
                                            ? true
                                            : false
                                      }
                                      size="small"
                                      className="boldlabel"
                                      name="jobCode"
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {loading ? (
                                              <CircularProgress size={20} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                />
                              </div>
                              {locationValue.id !== 0 &&
                                locationValue.type === "city" && (
                                  <div style={{ marginTop: "9px" }}>
                                    <small>Select Km radius</small>
                                    <Slider
                                      valueLabelDisplay="auto"
                                      value={locationRadius}
                                      step={10}
                                      min={0}
                                      max={100}
                                      style={{
                                        width: "92%",
                                        marginLeft: "11px",
                                      }}
                                      onChange={(e, value) =>
                                        setLocationRadius(value)
                                      }
                                    />
                                  </div>
                                )}
                            </div>
                          ) : item.name === "Candidate status" &&
                            item.type === "autoCompleteMulti" ? (
                            <Autocomplete
                              multiple
                              limitTags={2}
                              id="size-small-standard-multi"
                              size="small"
                              options={fieldOptions[item.name]}
                              getOptionLabel={(option) =>
                                typeof option === "string"
                                  ? option
                                  : (option?.internal ?? "")
                              }
                              value={statusObj?.["Candidate status"] || []}
                              isOptionEqualToValue={(option, value) => {
                                const optionId =
                                  typeof option === "string"
                                    ? option
                                    : option?.id;
                                const valueId =
                                  typeof value === "string" ? value : value?.id;
                                return optionId === valueId;
                              }}
                              onChange={(e, value) => {
                                let selectedValues = value.map((v) => v.id);
                                let selectedValueNames = value.map(
                                  (v) => v.internal || v.name,
                                );
                                setSavedAdvancedSearch({
                                  ...savedAdvancedSearch,
                                  autoCompleteMulti:
                                    selectedValueNames?.toString(),
                                });
                                onChangeHandler(
                                  value, //selectedValues,
                                  item.name,
                                  "options",
                                );
                              }}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Select" />
                              )}
                              sx={{
                                "& .MuiAutocomplete-clearIndicator": {
                                  padding: "4px",
                                },
                                "& .MuiAutocomplete-popupIndicator": {
                                  padding: "4px",
                                },
                              }}
                            />
                          ) : (item.name === "Source" ||
                              item.name === "recruiter") &&
                            item.type === "autoCompleteSingle" ? (
                            <Autocomplete
                              size="small"
                              options={
                                item.name === "Source"
                                  ? fieldOptions[item.name] || []
                                  : recruiterObj || []
                              }
                              value={
                                item.name === "Source"
                                  ? statusObj?.Source || null
                                  : recruiterObj?.find(
                                      (r) =>
                                        r.id ===
                                        savedAdvancedSearch?.recruiterId,
                                    ) || null
                              }
                              getOptionLabel={(option) =>
                                item.name === "Source"
                                  ? option
                                  : (option.name ?? "")
                              }
                              isOptionEqualToValue={(option, value) =>
                                item.name === "Source"
                                  ? option === value
                                  : option.id === value.id
                              }
                              onChange={(e, value) => {
                                if (item.name === "Source") {
                                  onChangeHandler(value, item.name, "options");
                                } else if (item.name === "recruiter") {
                                  setSavedAdvancedSearch((prev) => ({
                                    ...prev,
                                    recruiter: value?.name ?? "",
                                    recruiterId: value?.id ?? "",
                                  }));
                                  onChangeHandler(
                                    value?.id ?? "",
                                    "recruiterId",
                                    "",
                                  );
                                }
                              }}
                              sx={{
                                "& .MuiInputBase-input, & .MuiAutocomplete-input":
                                  {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                  },
                                "& .MuiAutocomplete-clearIndicator": {
                                  padding: "4px",
                                },
                                "& .MuiAutocomplete-popupIndicator": {
                                  padding: "4px",
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select"
                                  fullWidth
                                  size="small"
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    },
                                  }}
                                />
                              )}
                            />
                          ) : (
                            ""
                          )}
                        </div>
                      ) : (
                        ""
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="actionBtns advance-search-actionbtns">
              <Button
                type="button"
                className="cancelBtn"
                style={{ backgroundColor: "transparent" }}
                onClick={handleClearFilters}
                size="small"
              >
                Clear
              </Button>
              <Button
                className=" btnHover"
                variant="contained"
                size="small"
                type="submit"
                onClick={handleApplyFilter}
              >
                Filter
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

AdvancedSearch.propTypes = {
  onApplyFilter: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  customFieldsData: PropTypes.array,
  countriesData: PropTypes.array,
  recruiterList: PropTypes.array,
  fieldOptions: PropTypes.object,
  storedTheme: PropTypes.object,
  onClearFilters: PropTypes.func,
};

export default React.memo(AdvancedSearch);
